import { connectionManager } from './supabase/connectionManager';
import type { Database } from '../types/supabase';
import { Tournament, Player, Round, MatchPlay } from '../types';
import { saveToLocalStorage } from './localStorage';
import { cacheManager } from './cache';

// Track sync state
let isSyncing = false;
let syncQueue: Array<{ key: string; data: any }> = [];

// Real-time subscription setup
type SubscriptionCallback<T> = (payload: T) => void;
const subscriptions = new Map<string, SubscriptionCallback<any>>();
const activeChannels = new Map<string, ReturnType<typeof connectionManager.getClient>['channel']>();

export function subscribe<T>(channel: string, callback: SubscriptionCallback<T>) {
  subscriptions.set(channel, callback);
}

export function unsubscribe(channel: string) {
  subscriptions.delete(channel);
}

export function setupRealtimeSubscriptions() {
  const supabase = connectionManager.getClient();
  if (!supabase) {
    console.warn('Skipping realtime subscriptions - Supabase not configured');
    return;
  }

  if (!navigator.onLine) {
    console.log('Device is offline, will retry when online');
    return;
  }

  // Verify connection before setting up subscriptions
  connectionManager.connect().then(isConnected => {
    if (!isConnected) {
      console.warn('Failed to establish Supabase connection - subscriptions disabled');
      return;
    }

    // Add periodic retry for offline devices
    if (!navigator.onLine) {
      setTimeout(() => {
        setupRealtimeSubscriptions();
      }, 5000); // 5 seconds
      return;
    }

    setupChannels();
  });
  
  // Start periodic sync
  startPeriodicSync();
}

// Add periodic sync for tournaments
let syncInterval: number | null = null;

function startPeriodicSync() {
  if (syncInterval) return;
  
  syncInterval = window.setInterval(async () => {
    const supabase = connectionManager.getClient();
    if (!navigator.onLine || !supabase || !connectionManager.isConnected()) return;

    try {
      // Check cache and rate limits
      const cacheKey = 'tournaments';
      if (cacheManager.isRateLimited(cacheKey)) {
        const rateLimitInfo = cacheManager.getRateLimitInfo(cacheKey);
        console.warn(
          `Rate limit reached for tournaments. ` +
          `Remaining: ${rateLimitInfo?.remaining}, ` +
          `Reset in: ${rateLimitInfo?.resetIn}ms`
        );
        return;
      }

      // Check cache first
      const cachedData = cacheManager.get<Tournament[]>(cacheKey);
      if (cachedData) {
        console.log('Using cached tournament data');
        subscriptions.get('tournaments')?.(cachedData);
        return;
      }

      const { data: tournaments, error } = await supabase
        .from('tournaments')
        .select('*')
        .select('id, name, start_date, end_date, course_id, selected_tee, format, status')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (tournaments) {
        const formattedTournaments = tournaments.map(t => ({
          id: t.id,
          name: t.name,
          startDate: t.start_date,
          endDate: t.end_date,
          courseId: t.course_id,
          selectedTee: t.selected_tee,
          format: t.format
        }));

        saveToLocalStorage('hvga_tournaments', formattedTournaments);
        cacheManager.set(cacheKey, formattedTournaments);
        subscriptions.get('tournaments')?.(formattedTournaments);
      }
    } catch (error) {
      console.warn('Periodic tournament sync failed:', error);
    }
  }, 5000); // 5 seconds
}

function stopPeriodicSync() {
  if (syncInterval) {
    window.clearInterval(syncInterval);
    syncInterval = null;
  }
}

function setupChannels() {
  const supabase = connectionManager.getClient();
  if (!supabase) return;

  // Clean up existing subscriptions first
  cleanupSubscriptions();
  
  // Configure real-time subscription options
  const realtimeOptions = {
    config: {
      broadcast: {
        self: true // Receive own updates
      }
    }
  };

  const channelConfigs = [
    {
      name: 'tournaments',
      table: 'tournaments',
      query: 'id, name, start_date, end_date, course_id, selected_tee, format, status',
      order: 'created_at'
    },
    {
      name: 'players',
      table: 'players',
      query: '*',
      order: 'name'
    },
    {
      name: 'matches',
      table: 'matches',
      query: 'id, format, team1, team2, holes, status, result, date, starting_hole',
      order: 'date'
    }
  ];

  channelConfigs.forEach(config => {
    const channelId = `${config.name}`;
    console.log(`Setting up channel: ${channelId}`);

    const channel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: config.table },
        async () => {
          // Skip update if not connected
          if (!connectionManager.isConnected()) return;

          // Check rate limits
          if (cacheManager.isRateLimited(config.name)) {
            console.warn(`Rate limit reached for ${config.name}`);
            return;
          }

          try {
            const { data: newData, error } = await supabase
              .from(config.table)
              .select(config.query)
              .order(config.order || 'created_at');

            if (error) throw error;
            
            if (newData) {
              subscriptions.get(config.name)?.(newData);
            }
          } catch (error) {
            console.warn(`Error fetching ${config.name} data:`, error);
          }
        }
      )
      .subscribe();

    activeChannels.set(channelId, channel);
  });
}

function cleanupSubscriptions() {
  activeChannels.forEach(channel => {
    channel.unsubscribe();
  });
  activeChannels.clear();
}

export async function syncTournament(tournament: Tournament) {
  const supabase = connectionManager.getClient();
  if (!supabase) return;

  try {
    const { error } = await supabase
      .from('tournaments')
      .upsert({
        id: tournament.id,
        name: tournament.name,
        start_date: tournament.startDate,
        end_date: tournament.endDate,
        course_id: tournament.courseId,
        selected_tee: tournament.selectedTee,
        format: tournament.format,
        status: tournament.status,
        scoring_type: tournament.scoringType,
        metadata: tournament.metadata,
        player_limit: tournament.playerLimit,
        registration_deadline: tournament.registrationDeadline,
        rules: tournament.rules
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error syncing tournament:', error);
    throw error;
  }
}

export async function syncPlayers(players: Player[]) {
  const supabase = connectionManager.getClient();
  if (!supabase) return;

  try {
    const { error } = await supabase
      .from('players')
      .upsert(
        players.map(p => ({
          id: p.id,
          name: p.name,
          tournament_id: p.tournamentId,
          division: p.division,
          handicap: p.handicap,
          flight: p.flight,
          team: p.team,
          match_no: p.matchNo,
          starting_hole: p.startingHole,
          notes: p.notes,
          scores: p.scores,
          total_score: p.totalScore
        }))
      );

    if (error) throw error;
  } catch (error) {
    console.error('Error syncing players:', error);
    throw error;
  }
}

export async function syncMatches(matches: MatchPlay[]) {
  const supabase = connectionManager.getClient();
  if (!supabase) return;

  try {
    const { error } = await supabase
      .from('matches')
      .upsert(
        matches.map(m => ({
          id: m.id,
          tournament_id: m.tournamentId,
          format: m.format,
          team1: m.team1,
          team2: m.team2,
          holes: m.holes,
          status: m.status,
          result: m.result,
          date: m.date,
          starting_hole: m.startingHole
        }))
      );

    if (error) throw error;
  } catch (error) {
    console.error('Error syncing matches:', error);
    throw error;
  }
}

export async function syncRounds(rounds: Round[]) {
  const supabase = connectionManager.getClient();
  if (!supabase) return;

  try {
    const { error } = await supabase
      .from('rounds')
      .upsert(
        rounds.map(r => ({
          id: r.id,
          tournament_id: r.tournamentId,
          player_id: r.playerId,
          date: r.date,
          scores: r.scores,
          total_score: r.totalScore,
          notes: r.notes
        }))
      );

    if (error) throw error;
  } catch (error) {
    console.error('Error syncing rounds:', error);
    throw error;
  }
}

// Export the connection manager's client as supabase for backward compatibility
export const supabase = connectionManager.getClient();